import { useRef } from "react";
import { createPortal } from "react-dom";
import type { ContextMenuAnchor } from "./anchor";
import type { ContextMenuItem } from "./items";
import { MenuPanel } from "./MenuPanel";
import { useDismiss } from "./useDismiss";
import { useMenuCloser } from "./useMenuCloser";
import { rootPlacement } from "./usePanelPosition";

interface ContextMenuLayerProps {
  state: ContextMenuAnchor;
  items: ContextMenuItem[];
  onClose: () => void;
  ariaLabel: string;
}

const ignoreBack = (): void => undefined;

/** The open menu: a layer in <body> holding the root panel, dismissed by outside interaction. */
export function ContextMenuLayer({ state, items, onClose, ariaLabel }: ContextMenuLayerProps): React.JSX.Element {
  const layerRef = useRef<HTMLDivElement>(null);
  const closer = useMenuCloser(onClose, state.returnFocus);
  useDismiss(layerRef, closer.dismiss);

  return createPortal(
    <div ref={layerRef} className="nb-ctx-layer" onContextMenu={(e) => e.preventDefault()}>
      <MenuPanel
        key={state.openId}
        items={items}
        level={0}
        ariaLabel={ariaLabel}
        place={rootPlacement({ point: state.point, rect: state.rect })}
        closer={closer}
        initialFocus="first-item"
        onBack={ignoreBack}
        onEscape={closer.closeAndReturnFocus}
      />
    </div>,
    document.body,
  );
}
